/**
 * 화자 구분 엔진 — 등록 · 고치기 팝업 (2026-10-22 · 시안 SD2 와 디자이너 평 «묶음 2»).
 *
 * `SttEditModal` 과 같은 꼴에서 «모델» 칸만 뺐다 — 화자 구분 서비스는 모델을 받지 않는다
 * (서버 `diarize-adapters.ts` 의 `config` 는 `endpoint` · `timeout_sec` 둘이다).
 *
 * ## `config` 는 «통째로» 바뀐다 — 둘을 늘 함께 보낸다
 *
 * 서버 PATCH 는 `config` 를 부분 병합하지 않는다. 끝점만 보내면 `timeout_sec` 이 사라지고 기본값으로 돈다.
 * 그래서 이 팝업은 늘 둘을 함께 만든다(`config()`). 키는 `config` 에 넣지 않는다 — 암호화되지 않는 칸이다.
 *
 * ## 평문 http 는 «적는 그 자리»에서 말한다 — 막지는 않는다
 *
 * 끝점 칸 바로 아래에 한 줄. 저장한 뒤 표에서야 알면 결정하는 순간에는 위험을 모른다.
 * 사내 서버가 http 라 막으면 쓸 수가 없다 — 경고는 저장 단추의 잠김 조건에 들어가지 않는다.
 *
 * ## 연결 시험 결과는 두 줄
 *
 * 시험은 «무음 1초»를 올린다 — 확인되는 것은 «키와 경로가 맞다»까지다. 성공과 그 한계를 한 줄에
 * 같은 초록으로 붙이면 한계가 성공으로 읽힌다. 그래서 둘째 줄은 중립색으로 따로 적는다.
 */
import { useState } from 'react';
import { X, AlertTriangle, Check } from '../../../icons';
import {
  createProvider, patchProvider, deleteProvider, testKey,
  type ExternalApiAdapter, type ExternalApiProvider, type ProbeResult,
} from '../../../api/externalApis';
import { errText } from './ExternalApiEditModal';
import {
  DIARIZE_PATH, DIARIZE_PLAINTEXT_WARNING, DIARIZE_TIMEOUT_DEFAULT_SEC, DIARIZE_TIMEOUT_MAX_SEC, DIARIZE_TIMEOUT_MIN_SEC,
  checkDiarizeEndpoint, checkDiarizeTimeout, formatDuration, readDiarizeConfig,
} from './diarizeEndpoint';
import { diarizeFixText } from './DiarizePanel';
import './Diarize.css';

interface Props {
  /** null 이면 새로 등록. */
  provider: ExternalApiProvider | null;
  /** 고를 수 있는 것 — `category === 'diarize'` 이고 아직 등록 안 된 것. */
  adapters: ExternalApiAdapter[];
  onClose: () => void;
  onSaved: (message: string) => void;
}

export function DiarizeEditModal({ provider, adapters, onClose, onSaved }: Props) {
  const isNew = provider === null;
  const choices = adapters.filter((a) => a.category === 'diarize' && !a.registered);
  const saved = provider ? readDiarizeConfig(provider) : null;

  const [providerKey, setProviderKey] = useState(isNew ? (choices[0]?.provider_key ?? '') : provider.provider_key);
  const [endpoint, setEndpoint] = useState(saved?.endpoint ?? '');
  const [timeout, setTimeout_] = useState(saved ? String(saved.timeout_sec) : String(DIARIZE_TIMEOUT_DEFAULT_SEC));
  const [apiKey, setApiKey] = useState('');
  const [enabled, setEnabled] = useState(provider?.enabled ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [probe, setProbe] = useState<ProbeResult | null>(null);

  const ep = checkDiarizeEndpoint(endpoint);
  const to = checkDiarizeTimeout(timeout);
  // 적는 중에는 나무라지 않는다 — 비어 있는 것은 「아직 안 적었다」이지 틀린 것이 아니다.
  const epError = endpoint.trim() !== '' && !ep.ok ? ep.message : null;
  const toError = !to.ok ? to.message : null;
  const keyMissing = apiKey.trim() === '';

  /** 늘 둘을 함께 만든다 — 서버가 `config` 를 통째로 바꾸기 때문이다. 키는 여기 들어가지 않는다. */
  function config(): Record<string, unknown> | null {
    if (!ep.ok || !to.ok) return null;
    return { endpoint: ep.value, timeout_sec: to.value };
  }

  async function runTest() {
    const cfg = config();
    if (keyMissing) { setError('시험할 키를 넣으세요.'); return; }
    if (!cfg) { setError(epError ?? toError ?? (ep.ok ? '타임아웃을 다시 보세요.' : ep.message)); return; }
    setBusy(true); setError(null); setProbe(null);
    try {
      // 저장 «전» 시험에도 끝점을 함께 보낸다 — 어디로 부를지 없이 재면 무엇을 잰 것인지 모른다.
      setProbe(await testKey(providerKey, apiKey.trim(), cfg));
    } catch (e: unknown) {
      setError(errText(e, '연결 시험에 실패했습니다'));
    } finally { setBusy(false); }
  }

  async function save() {
    const cfg = config();
    if (!cfg) {
      setError(epError ?? toError ?? (ep.ok ? '타임아웃을 다시 보세요.' : ep.message));
      return;
    }
    setBusy(true); setError(null);
    try {
      if (isNew) {
        if (!providerKey) { setError('엔진 종류를 고르십시오.'); setBusy(false); return; }
        await createProvider({
          provider_key: providerKey,
          enabled,
          config: cfg,
          ...(apiKey.trim() ? { api_key: apiKey.trim() } : {}),
        });
        onSaved('화자 구분 엔진을 등록했습니다 — 연결 시험으로 키를 확인하십시오.');
      } else {
        await patchProvider(provider.provider_key, {
          enabled,
          config: cfg,
          // 비우면 «안 바꾼다» — 다른 공급자 팝업과 같은 규칙.
          ...(apiKey.trim() ? { api_key: apiKey.trim() } : {}),
        });
        onSaved('저장했습니다 — 연결 시험으로 키를 확인하십시오.');
      }
    } catch (e: unknown) {
      setError(errText(e, '저장하지 못했습니다'));
      setBusy(false);
    }
  }

  async function removeProvider() {
    if (isNew) return;
    setBusy(true); setError(null);
    try {
      await deleteProvider(provider.provider_key);
      onSaved('화자 구분 엔진을 지웠습니다.');
    } catch (e: unknown) {
      setError(errText(e, '지우지 못했습니다'));
      setBusy(false);
    }
  }

  const title = isNew ? '화자 구분 엔진 등록' : provider.label;

  return (
    <div className="cl-modal__backdrop" role="presentation" onMouseDown={onClose}>
      <div
        className="cl-modal xa-dz-modal"
        role="dialog"
        aria-modal="true"
        aria-label={title}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="cl-modal__head">
          <h3>{title}</h3>
          <button type="button" className="cl-modal__close" aria-label="닫기" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="cl-modal__body">
          <div className="cl-field">
            <label htmlFor={isNew ? 'dz-engine' : undefined}>엔진 종류</label>
            {isNew
              ? (
                <select id="dz-engine" className="xa-select" value={providerKey} onChange={(e) => { setProviderKey(e.target.value); setProbe(null); }}>
                  {choices.length === 0 && <option value="">등록할 수 있는 엔진이 없습니다</option>}
                  {choices.map((a) => <option key={a.provider_key} value={a.provider_key}>{a.default_label}</option>)}
                </select>
              )
              : (
                <div id="dz-engine" className="xa-readonly">
                  <span>{adapters.find((a) => a.provider_key === provider.provider_key)?.default_label ?? provider.label}</span>
                  <span className="xa-dz-sub">{provider.provider_key}</span>
                </div>
              )}
          </div>

          <div className="cl-field">
            <label htmlFor="dz-endpoint">끝점</label>
            <input
              id="dz-endpoint"
              className="xa-input"
              value={endpoint}
              placeholder="http://호스트:포트"
              onChange={(e) => { setEndpoint(e.target.value); setProbe(null); }}
            />
            <p className="xa-hint">
              호스트까지만 적으면 {DIARIZE_PATH} 를 붙입니다. 받아쓰기와 다른 포트입니다.
              {ep.ok && <><br />실제로는 <b>{ep.callUrl}</b> 를 부릅니다.</>}
            </p>
            {epError && <p className="xa-hint xa-danger" role="alert">{epError}</p>}
            {/* 막지 않고 말만 한다 — https 에는 띄우지 않는다(늘 뜨는 경고는 아무도 안 읽는다). */}
            {ep.ok && ep.plaintext && (
              <p className="xa-hint xa-dz-warn" role="status">
                <AlertTriangle size={14} aria-hidden />
                <span><b>평문 http</b> — {DIARIZE_PLAINTEXT_WARNING}</span>
              </p>
            )}
          </div>

          <div className="cl-field">
            <label htmlFor="dz-timeout">타임아웃(초)</label>
            <input
              id="dz-timeout"
              className="xa-input"
              inputMode="numeric"
              value={timeout}
              onChange={(e) => setTimeout_(e.target.value)}
            />
            <p className="xa-hint">
              녹음이 끝난 뒤 한 번 부릅니다 · 1시간 녹음 약 2~3분 · 앞 건을 기다릴 수 있어
              넉넉히({DIARIZE_TIMEOUT_MIN_SEC}~{DIARIZE_TIMEOUT_MAX_SEC})
              {to.ok && <> · <b className="xa-dz-eq">= {formatDuration(to.value)}</b></>}
            </p>
            {toError && <p className="xa-hint xa-danger" role="alert">{toError}</p>}
          </div>

          {!isNew && (
            <div className="cl-field">
              <label>지금 키</label>
              <div className="xa-readonly">
                {provider.key_state === 'stored' && (provider.key_hint
                  ? <b>{provider.key_hint}</b>
                  : <span>저장돼 있습니다 · 끝 넉 자는 다음에 키를 넣을 때부터 보입니다</span>)}
                {provider.key_state === 'none' && <span>등록된 키가 없습니다</span>}
                {provider.key_state === 'unreadable' && <span className="xa-badge xa-badge--bad">키를 못 읽음 — 다시 넣어야 합니다</span>}
              </div>
            </div>
          )}

          <div className="cl-field">
            <label htmlFor="dz-key">{isNew ? '키' : '새 키로 교체'}</label>
            <input
              id="dz-key"
              className="xa-input"
              type="password"
              autoComplete="off"
              value={apiKey}
              onChange={(e) => { setApiKey(e.target.value); setProbe(null); }}
            />
            {/* 도움말은 칸 아래 글로 — 자리표시 글자는 적기 시작하면 사라진다. */}
            <p className="xa-hint">
              서비스에 적어 둔 인증키(받아쓰기 키와 따로)입니다.
              {isNew
                ? ' 저장하면 다시 보이지 않고 끝 네 자리만 남습니다.'
                : ' 비우면 지금 키를 그대로 둡니다. 바꾸면 옛 키는 되돌릴 수 없습니다.'}
            </p>
          </div>

          <label className="xa-check">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            켜기
          </label>

          {probe && (
            <div className="xa-dz-probe" role="status">
              {probe.verdict === 'ok'
                ? (
                  <b className="xa-dz-probe__ok">
                    <Check size={16} aria-hidden />
                    연결 정상 — 키를 받아들였습니다
                  </b>
                )
                : (
                  <b className="xa-dz-probe__bad">
                    <AlertTriangle size={16} aria-hidden />
                    {diarizeFixText(probe.verdict)}{probe.http_status ? ` · HTTP ${probe.http_status}` : ''}
                  </b>
                )}
              <span className="xa-dz-probe__note">
                {probe.verdict === 'ok'
                  ? '시험은 무음 1초라 화자를 잘 가르는지는 모릅니다'
                  : probe.message}
              </span>
            </div>
          )}
          {error && (
            <div className="xa-band xa-band--bad" role="alert">
              <AlertTriangle size={18} aria-hidden />
              <span className="xa-band__t"><b>{error}</b></span>
            </div>
          )}
          {keyMissing && <p className="xa-hint">연결 시험은 키를 넣어야 할 수 있습니다 — 키를 넣으세요.</p>}
        </div>

        <div className="cl-modal__foot">
          <button type="button" className="cl-modal__btn" disabled={busy} onClick={onClose}>취소</button>
          {!isNew && <button type="button" className="cl-modal__btn xa-danger" disabled={busy} onClick={() => void removeProvider()}>엔진 삭제</button>}
          <span className="xa-spacer" />
          <button
            type="button"
            className="cl-modal__btn"
            disabled={busy || keyMissing || !ep.ok || !to.ok || !providerKey}
            onClick={() => void runTest()}
          >
            연결 시험
          </button>
          <button
            type="button"
            className="cl-modal__btn cl-modal__btn--primary"
            disabled={busy || !ep.ok || !to.ok || !providerKey}
            onClick={() => void save()}
          >
            저장
          </button>
        </div>
      </div>
    </div>
  );
}
